import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight,
  Briefcase,
  CheckCircle2,
  FileText,
  Loader2,
  MessageCircle,
  Search,
  Send,
  Sparkles,
  User,
} from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { trackCTA } from '../../../utils/metaPixel';

type DemoStepId = 'find' | 'optimize' | 'apply' | 'prepare';

interface DemoStep {
  id: DemoStepId;
  label: string;
  icon: React.FC<{ className?: string }>;
  outcome: string;
}

const DEMO_STEPS: DemoStep[] = [
  { id: 'find', label: 'Find jobs', icon: Search, outcome: '8 matches' },
  { id: 'optimize', label: 'Optimize resume', icon: FileText, outcome: 'ATS 67 → 91' },
  { id: 'apply', label: 'Review & apply', icon: Send, outcome: 'Pack ready' },
  { id: 'prepare', label: 'Prepare', icon: MessageCircle, outcome: '15 interview questions' },
];

const RAHUL_SKILLS = ['Java', 'Spring Boot', 'AWS', 'Microservices', 'Kafka'];

const DEMO_JOBS = [
  { title: 'Senior Java Engineer', company: 'Flipkart', match: 92 },
  { title: 'Backend Engineer', company: 'Razorpay', match: 88 },
  { title: 'Staff Engineer', company: 'PhonePe', match: 85 },
  { title: 'Java Developer', company: 'Swiggy', match: 81 },
];

const SAMPLE_QUESTIONS = [
  'How would you design a rate-limiter for an API gateway?',
  'Explain your experience scaling Spring Boot services on AWS.',
  'Walk through a production incident you debugged end-to-end.',
];

interface HomeInteractiveDemoSectionProps {
  onNavigate?: (view: string) => void;
}

function StepPreview({
  step,
  loading,
}: {
  step: DemoStepId | null;
  loading: boolean;
}) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="home-interactive-demo__preview-loading">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--ds-marketing-accent, #5F8568)' }} />
        <span className="t-meta">{t('home.interactiveDemo.running', 'Running demo step…')}</span>
      </div>
    );
  }

  if (!step) {
    return (
      <div className="home-interactive-demo__preview-empty">
        <Sparkles className="w-8 h-8" style={{ color: 'var(--ds-marketing-accent, #5F8568)' }} aria-hidden />
        <p className="home-interactive-demo__preview-empty-title">
          {t('home.interactiveDemo.pickStep', 'Click a step to see what happens')}
        </p>
        <p className="t-meta">
          {t('home.interactiveDemo.pickStepHint', 'No signup needed — this is a guided preview.')}
        </p>
      </div>
    );
  }

  if (step === 'find') {
    return (
      <motion.div
        key="find"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        className="home-interactive-demo__preview-panel"
      >
        <div className="home-interactive-demo__preview-badge home-interactive-demo__preview-badge--ok">
          <Briefcase className="w-3.5 h-3.5" aria-hidden />
          {t('home.interactiveDemo.find.result', '8 matches found')}
        </div>
        <p className="home-interactive-demo__preview-lede">
          {t('home.interactiveDemo.find.lede', 'Ranked by skills, experience, and location fit for Rahul.')}
        </p>
        <ul className="home-interactive-demo__job-list">
          {DEMO_JOBS.map((job) => (
            <li key={job.company} className="home-interactive-demo__job-row">
              <div>
                <p className="home-interactive-demo__job-title">{job.title}</p>
                <p className="t-meta">{job.company}</p>
              </div>
              <span className="home-interactive-demo__match-pill t-num">{job.match}%</span>
            </li>
          ))}
        </ul>
        <p className="t-meta home-interactive-demo__preview-foot">
          {t('home.interactiveDemo.find.more', '+ 4 more matches in the full workspace')}
        </p>
      </motion.div>
    );
  }

  if (step === 'optimize') {
    return (
      <motion.div
        key="optimize"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="home-interactive-demo__preview-panel"
      >
        <div className="home-interactive-demo__ats-row">
          <div className="home-interactive-demo__ats-score">
            <span className="t-meta">{t('home.interactiveDemo.optimize.before', 'Before')}</span>
            <span className="home-interactive-demo__ats-num t-num">67</span>
          </div>
          <ArrowRight className="w-4 h-4 shrink-0 home-interactive-demo__ats-arrow" aria-hidden />
          <div className="home-interactive-demo__ats-score home-interactive-demo__ats-score--after">
            <span className="t-meta">{t('home.interactiveDemo.optimize.after', 'After')}</span>
            <span className="home-interactive-demo__ats-num t-num">91</span>
          </div>
        </div>
        <div className="home-interactive-demo__ats-bar" aria-hidden>
          <div className="home-interactive-demo__ats-bar-before" style={{ width: '67%' }} />
          <div className="home-interactive-demo__ats-bar-after" style={{ width: '91%' }} />
        </div>
        <ul className="home-interactive-demo__fix-list">
          <li><CheckCircle2 className="w-3.5 h-3.5" aria-hidden /> Added missing JD keywords: Kubernetes, CI/CD</li>
          <li><CheckCircle2 className="w-3.5 h-3.5" aria-hidden /> Quantified impact on 3 bullet points</li>
          <li><CheckCircle2 className="w-3.5 h-3.5" aria-hidden /> Fixed format issues flagged by ATS scan</li>
        </ul>
      </motion.div>
    );
  }

  if (step === 'apply') {
    return (
      <motion.div
        key="apply"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="home-interactive-demo__preview-panel"
      >
        <div className="home-interactive-demo__apply-success">
          <CheckCircle2 className="w-10 h-10" aria-hidden />
          <p className="home-interactive-demo__apply-title">
            {t('home.interactiveDemo.apply.title', 'Application created')}
          </p>
          <p className="t-meta">
            {t('home.interactiveDemo.apply.subtitle', 'Senior Java Engineer · Flipkart')}
          </p>
        </div>
        <div className="home-interactive-demo__apply-meta">
          <span>{t('home.interactiveDemo.apply.resume', 'Resume v3 (ATS 91)')}</span>
          <span>{t('home.interactiveDemo.apply.status', 'Status: Applied')}</span>
          <span>{t('home.interactiveDemo.apply.tracked', 'Tracked in My Activities')}</span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      key="prepare"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="home-interactive-demo__preview-panel"
    >
      <div className="home-interactive-demo__preview-badge home-interactive-demo__preview-badge--purple">
        <MessageCircle className="w-3.5 h-3.5" aria-hidden />
        {t('home.interactiveDemo.prepare.result', '15 interview questions ready')}
      </div>
      <p className="home-interactive-demo__preview-lede">
        {t('home.interactiveDemo.prepare.lede', 'Tailored to the Flipkart JD and Rahul\'s resume gaps.')}
      </p>
      <ol className="home-interactive-demo__question-list">
        {SAMPLE_QUESTIONS.map((q, i) => (
          <li key={i}>
            <span className="home-interactive-demo__q-num t-num">{i + 1}</span>
            <span>{q}</span>
          </li>
        ))}
      </ol>
      <p className="t-meta home-interactive-demo__preview-foot">
        {t('home.interactiveDemo.prepare.more', '+ 12 more in Interview Prep')}
      </p>
    </motion.div>
  );
}

const HomeInteractiveDemoSection: React.FC<HomeInteractiveDemoSectionProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState<DemoStepId | null>(null);
  const [completed, setCompleted] = useState<Set<DemoStepId>>(new Set());
  const [loading, setLoading] = useState(false);

  const runStep = useCallback((stepId: DemoStepId) => {
    trackCTA(`home_interactive_demo_${stepId}`);
    setLoading(true);
    setActiveStep(null);
    window.setTimeout(() => {
      setActiveStep(stepId);
      setCompleted((prev) => new Set(prev).add(stepId));
      setLoading(false);
    }, 520);
  }, []);

  const allDone = completed.size === DEMO_STEPS.length;

  return (
    <section
      id="interactive-demo"
      className="home-interactive-demo scroll-mt-28"
      aria-labelledby="home-interactive-demo-title"
    >
      <div className="home-interactive-demo__container">
        <div className="home-interactive-demo__header">
          <p className="home-interactive-demo__eyebrow">
            {t('home.interactiveDemo.eyebrow', 'See it before you sign up')}
          </p>
          <h2 id="home-interactive-demo-title" className="home-interactive-demo__title">
            {t('home.interactiveDemo.title', 'Try the workflow in 30 seconds')}
          </h2>
          <p className="home-interactive-demo__subtitle">
            {t(
              'home.interactiveDemo.subtitle',
              'Follow Rahul through find → optimize → apply → prepare. Interactive preview — no account required.',
            )}
          </p>
        </div>

        <div className="home-interactive-demo__layout">
          <aside className="home-interactive-demo__sidebar">
            <div className="home-interactive-demo__candidate">
              <div className="home-interactive-demo__avatar" aria-hidden>
                <User className="w-5 h-5" />
              </div>
              <div>
                <p className="home-interactive-demo__candidate-label">
                  {t('home.interactiveDemo.meet', 'Meet')}
                </p>
                <p className="home-interactive-demo__candidate-name">Rahul</p>
                <p className="home-interactive-demo__candidate-meta t-meta">
                  {t('home.interactiveDemo.candidateMeta', '7 years experience')}
                </p>
              </div>
              <span className="home-interactive-demo__demo-pill">
                {t('home.interactiveDemo.demoBadge', 'Demo')}
              </span>
            </div>

            <div className="home-interactive-demo__skills">
              {RAHUL_SKILLS.map((skill) => (
                <span key={skill} className="home-interactive-demo__skill-chip">{skill}</span>
              ))}
            </div>

            <div className="home-interactive-demo__steps" role="list">
              {DEMO_STEPS.map((step, index) => {
                const Icon = step.icon;
                const isActive = activeStep === step.id;
                const isDone = completed.has(step.id);
                return (
                  <button
                    key={step.id}
                    type="button"
                    role="listitem"
                    className={`home-interactive-demo__step${isActive ? ' home-interactive-demo__step--active' : ''}${isDone ? ' home-interactive-demo__step--done' : ''}`}
                    onClick={() => runStep(step.id)}
                    disabled={loading}
                    aria-current={isActive ? 'step' : undefined}
                  >
                    <span className="home-interactive-demo__step-index t-num">{index + 1}</span>
                    <span className="home-interactive-demo__step-icon" aria-hidden>
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="home-interactive-demo__step-copy">
                      <span className="home-interactive-demo__step-label">{step.label}</span>
                      <span className="home-interactive-demo__step-outcome t-meta">→ {step.outcome}</span>
                    </span>
                    {isDone ? (
                      <CheckCircle2 className="w-4 h-4 shrink-0 home-interactive-demo__step-check" aria-hidden />
                    ) : null}
                  </button>
                );
              })}
            </div>

            {allDone && onNavigate ? (
              <button
                type="button"
                className="home-interactive-demo__cta home-marketing-cta"
                onClick={() => {
                  trackCTA('home_interactive_demo_signup');
                  onNavigate('signup');
                }}
              >
                {t('home.interactiveDemo.cta', 'Start free with your profile')}
                <ArrowRight className="w-4 h-4" aria-hidden />
              </button>
            ) : null}
          </aside>

          <div className="home-interactive-demo__preview" aria-live="polite">
            <div className="home-interactive-demo__preview-chrome">
              <span className="home-interactive-demo__chrome-dot" />
              <span className="home-interactive-demo__chrome-dot" />
              <span className="home-interactive-demo__chrome-dot" />
              <span className="home-interactive-demo__chrome-title">
                {t('home.interactiveDemo.previewTitle', 'GoGrabJob workspace preview')}
              </span>
            </div>
            <AnimatePresence mode="wait">
              <StepPreview step={activeStep} loading={loading} />
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default React.memo(HomeInteractiveDemoSection);
